import { useEffect, useRef } from "react";
import { InfiniteData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { queryClient } from "../utils/QueryClient";

import { addStompConnectListener, isStompConnected, subscribeStomp } from "../utils/stompClient";
import { ChatMessageType } from "../models/chat/ChatMessageDto";
import { ChatGroupEventDto, ChatGroupListDto, ChatGroupMessageDto } from "../models/chat/ChatGroup";
import { ChatGroupMemberRole, CreateChatGroupRequest } from "../models/chat/ChatGroup";
import { RootState } from "../stores/Store";
import { chatGroupService } from "../services/ChatGroupService";

export const GROUP_CHAT_PAGE_SIZE = 30;

const GROUPS_KEY = ["chat", "groups"];
const groupDetailKey = (groupId: string) => ["chat", "group-detail", groupId];
const groupMessagesKey = (groupId: string) => ["chat", "group-messages", groupId];

const invalidateGroups = () => queryClient.invalidateQueries({ queryKey: GROUPS_KEY });
const invalidateGroupDetail = (groupId: string) => queryClient.invalidateQueries({ queryKey: groupDetailKey(groupId) });

const appendGroupMessage = (groupId: string, message: ChatGroupMessageDto) => {
  queryClient.setQueryData<InfiniteData<ChatGroupMessageDto[]>>(groupMessagesKey(groupId), (old) => {
    if (!old || old.pages.length === 0) return old;
    if (old.pages.some((page) => page.some((m) => m.id === message.id))) return old;
    return { ...old, pages: [[message, ...old.pages[0]], ...old.pages.slice(1)] };
  });
};

const revokeBlobUrls = (message?: ChatGroupMessageDto) => {
  message?.attachments?.forEach((a) => {
    if (a.url?.startsWith("blob:")) URL.revokeObjectURL(a.url);
  });
};

const findGroupMessage = (groupId: string, id: string): ChatGroupMessageDto | undefined =>
  queryClient
    .getQueryData<InfiniteData<ChatGroupMessageDto[]>>(groupMessagesKey(groupId))
    ?.pages.flat()
    .find((m) => m.id === id);

const reconcileSentMessage = (groupId: string, tempId: string, real: ChatGroupMessageDto) => {
  revokeBlobUrls(findGroupMessage(groupId, tempId));
  queryClient.setQueryData<InfiniteData<ChatGroupMessageDto[]>>(groupMessagesKey(groupId), (old) => {
    if (!old) return old;
    const realExists = old.pages.some((page) => page.some((m) => m.id === real.id));
    return {
      ...old,
      pages: old.pages.map((page) =>
        page.flatMap((m) => (m.id !== tempId ? [m] : realExists ? [] : [{ ...real, status: undefined }])),
      ),
    };
  });
};

const updateMessageStatus = (groupId: string, id: string, status: ChatGroupMessageDto["status"]) => {
  queryClient.setQueryData<InfiniteData<ChatGroupMessageDto[]>>(groupMessagesKey(groupId), (old) => {
    if (!old) return old;
    return { ...old, pages: old.pages.map((page) => page.map((m) => (m.id === id ? { ...m, status } : m))) };
  });
};

export const removeGroupMessage = (groupId: string, id: string) => {
  revokeBlobUrls(findGroupMessage(groupId, id));
  queryClient.setQueryData<InfiniteData<ChatGroupMessageDto[]>>(groupMessagesKey(groupId), (old) => {
    if (!old) return old;
    return { ...old, pages: old.pages.map((page) => page.filter((m) => m.id !== id)) };
  });
};

export const makeTempMessageId = (): string =>
  "temp-" + (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

const groupPreview = (m: ChatGroupMessageDto): string => {
  if (m.messageType === "ATTACHMENT" && (!m.content || m.content.trim() === "")) return "[Tệp đính kèm]";
  return m.content;
};

const bumpGroupList = (message: ChatGroupMessageDto, fromMe: boolean, nowMs: number): boolean => {
  const list = queryClient.getQueryData<ChatGroupListDto[]>(GROUPS_KEY);
  if (!list || !list.some((g) => g.id === message.groupId)) return false;
  const next = list
    .map((g) =>
      g.id === message.groupId
        ? {
            ...g,
            lastMessageContent: groupPreview(message),
            lastMessageType: message.messageType ?? "TEXT",
            lastMessageSenderName: message.senderName ?? g.lastMessageSenderName,
            lastMessageTime: message.registeredTime,
            lastMessageEpochMs: nowMs,
            unreadCount: fromMe || message.messageType === "SYSTEM" ? g.unreadCount : g.unreadCount + 1,
          }
        : g,
    )
    .sort((a, b) => (b.lastMessageEpochMs ?? 0) - (a.lastMessageEpochMs ?? 0));
  queryClient.setQueryData(GROUPS_KEY, next);
  return true;
};

const clearGroupUnread = (groupId: string) => {
  queryClient.setQueryData<ChatGroupListDto[]>(GROUPS_KEY, (old) => old?.map((g) => (g.id === groupId ? { ...g, unreadCount: 0 } : g)));
};

const markGroupSeen = (groupId: string, readerUserId: string) => {
  queryClient.setQueryData<InfiniteData<ChatGroupMessageDto[]>>(groupMessagesKey(groupId), (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page) =>
        page.map((m) => {
          if (m.senderUserId === readerUserId) return m;
          const seen = m.seenByUserIds ?? [];
          return seen.includes(readerUserId) ? m : { ...m, seenByUserIds: [...seen, readerUserId] };
        }),
      ),
    };
  });
};

export const useMyGroups = (enabled = true) =>
  useQuery({ queryKey: GROUPS_KEY, queryFn: chatGroupService.listMyGroups, enabled, staleTime: 15_000 });

export const useGroupDetail = (groupId: string, enabled = true) =>
  useQuery({ queryKey: groupDetailKey(groupId), queryFn: () => chatGroupService.getGroupDetail(groupId), enabled, staleTime: 30_000 });

export const useGroupMessages = (groupId: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: groupMessagesKey(groupId),
    queryFn: ({ pageParam }) => chatGroupService.getGroupMessages(groupId, pageParam, GROUP_CHAT_PAGE_SIZE),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.length === GROUP_CHAT_PAGE_SIZE ? lastPage[lastPage.length - 1].id : undefined),
    enabled,
    staleTime: Infinity,
  });

interface SendGroupTextVars {
  groupId: string;
  content: string;
  messageType?: ChatMessageType;
  tempId: string;
}

export const useSendGroupMessage = () => {
  const myUserId = useSelector((state: RootState) => state.auth.userId);
  return useMutation({
    mutationFn: ({ groupId, content, messageType = "TEXT" }: SendGroupTextVars) =>
      chatGroupService.sendGroupMessage(groupId, content, messageType),
    onMutate: ({ groupId, content, messageType = "TEXT", tempId }) => {
      appendGroupMessage(groupId, {
        id: tempId,
        groupId,
        senderUserId: myUserId ?? "",
        content,
        messageType,
        registeredTime: "",
        seenByUserIds: [],
        status: "sending",
      });
    },
    onSuccess: (message, { groupId, tempId }) => {
      reconcileSentMessage(groupId, tempId, message);
      if (!bumpGroupList(message, true, Date.now())) invalidateGroups();
    },
    onError: (_err, { groupId, tempId }) => {
      updateMessageStatus(groupId, tempId, "failed");
    },
  });
};

interface SendGroupAttachmentVars {
  groupId: string;
  files: File[];
  content?: string;
  tempId: string;
}

export const useSendGroupAttachment = () => {
  const myUserId = useSelector((state: RootState) => state.auth.userId);
  return useMutation({
    mutationFn: ({ groupId, files, content }: SendGroupAttachmentVars) => chatGroupService.sendGroupAttachment(groupId, files, content),
    onMutate: ({ groupId, files, content, tempId }) => {
      const attachments = files.map((f, i) => {
        const isImage = f.type.startsWith("image/");
        return {
          id: `${tempId}-${i}`,
          originalFileName: f.name,
          fileSize: f.size,
          contentType: f.type,
          isImage,
          url: isImage ? URL.createObjectURL(f) : null,
        };
      });
      appendGroupMessage(groupId, {
        id: tempId,
        groupId,
        senderUserId: myUserId ?? "",
        content: content ?? "",
        messageType: "ATTACHMENT",
        registeredTime: "",
        attachments,
        seenByUserIds: [],
        status: "sending",
      });
    },
    onSuccess: (message, { groupId, tempId }) => {
      reconcileSentMessage(groupId, tempId, message);
      if (!bumpGroupList(message, true, Date.now())) invalidateGroups();
    },
    onError: (_err, { groupId, tempId }) => {
      updateMessageStatus(groupId, tempId, "failed");
    },
  });
};

export const useMarkGroupRead = () =>
  useMutation({
    mutationFn: (groupId: string) => chatGroupService.markGroupRead(groupId),
    onSuccess: (_data, groupId) => clearGroupUnread(groupId),
  });

export const useCreateGroup = () =>
  useMutation({
    mutationFn: (request: CreateChatGroupRequest) => chatGroupService.createGroup(request),
    onSuccess: (detail) => {
      queryClient.setQueryData<ChatGroupListDto[]>(GROUPS_KEY, (old) => {
        if (!old) return old;
        if (old.some((g) => g.id === detail.id)) return old;
        const newGroup: ChatGroupListDto = {
          id: detail.id,
          name: detail.name,
          avatarUrl: detail.avatarUrl,
          memberCount: detail.members.length,
          unreadCount: 0,
          lastMessageEpochMs: Date.now(),
          lastMessageTime: "Vừa tạo",
          lastMessageContent: null,
          myRole: "ADMIN",
        };
        return [newGroup, ...old];
      });
      invalidateGroups();
    },
  });

export const useRenameGroup = () =>
  useMutation({
    mutationFn: ({ groupId, name }: { groupId: string; name: string }) => chatGroupService.renameGroup(groupId, name),
    onSuccess: (detail) => {
      queryClient.setQueryData(groupDetailKey(detail.id), detail);
      invalidateGroups();
    },
  });

export const useUpdateGroupAvatar = () =>
  useMutation({
    mutationFn: ({ groupId, file }: { groupId: string; file: File }) => chatGroupService.updateGroupAvatar(groupId, file),
    onSuccess: (detail) => {
      queryClient.setQueryData(groupDetailKey(detail.id), detail);
      invalidateGroups();
    },
  });

export const useAddGroupMembers = () =>
  useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: string; userIds: string[] }) => chatGroupService.addGroupMembers(groupId, userIds),
    onSuccess: (detail) => {
      queryClient.setQueryData(groupDetailKey(detail.id), detail);
      invalidateGroups();
    },
  });

export const useRemoveGroupMember = () =>
  useMutation({
    mutationFn: ({ groupId, targetUserId }: { groupId: string; targetUserId: string }) =>
      chatGroupService.removeGroupMember(groupId, targetUserId),
    onSuccess: (_data, { groupId }) => {
      invalidateGroupDetail(groupId);
      invalidateGroups();
    },
  });

export const useChangeGroupMemberRole = () =>
  useMutation({
    mutationFn: ({ groupId, targetUserId, role }: { groupId: string; targetUserId: string; role: ChatGroupMemberRole }) =>
      chatGroupService.changeGroupMemberRole(groupId, targetUserId, role),
    onSuccess: (_data, { groupId }) => {
      invalidateGroupDetail(groupId);
      invalidateGroups();
    },
  });

export const useLeaveGroup = () =>
  useMutation({
    mutationFn: (groupId: string) => chatGroupService.leaveGroup(groupId),
    onSuccess: (_data, groupId) => {
      queryClient.removeQueries({ queryKey: groupDetailKey(groupId) });
      queryClient.removeQueries({ queryKey: groupMessagesKey(groupId) });
      invalidateGroups();
    },
  });

export const useDisbandGroup = () =>
  useMutation({
    mutationFn: (groupId: string) => chatGroupService.disbandGroup(groupId),
    onSuccess: (_data, groupId) => {
      queryClient.removeQueries({ queryKey: groupDetailKey(groupId) });
      queryClient.removeQueries({ queryKey: groupMessagesKey(groupId) });
      invalidateGroups();
    },
  });

const isGroupEvent = (payload: ChatGroupMessageDto | ChatGroupEventDto): payload is ChatGroupEventDto =>
  typeof (payload as ChatGroupEventDto).event === "string";

export const useChatGroupSocket = (
  enabled: boolean,
  onIncoming?: (message: ChatGroupMessageDto) => void,
  onGroupGone?: (groupId: string) => void,
) => {
  const myUserId = useSelector((state: RootState) => state.auth.userId);
  const onIncomingRef = useRef(onIncoming);
  onIncomingRef.current = onIncoming;
  const onGroupGoneRef = useRef(onGroupGone);
  onGroupGoneRef.current = onGroupGone;

  useEffect(() => {
    if (!enabled || !myUserId) return;
    let skipNextConnect = !isStompConnected();
    const offConnect = addStompConnectListener(() => {
      if (skipNextConnect) {
        skipNextConnect = false;
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["chat"] });
    });
    const offMessage = subscribeStomp<ChatGroupMessageDto | ChatGroupEventDto>("/user/queue/chat-group", (payload) => {
      if (isGroupEvent(payload)) {
        if (payload.event === "READ") {
          markGroupSeen(payload.groupId, payload.actorUserId);
          return;
        }
        if (payload.event === "DELETED" || (payload.event === "LEFT" && payload.actorUserId === myUserId)) {
          queryClient.removeQueries({ queryKey: groupDetailKey(payload.groupId) });
          queryClient.removeQueries({ queryKey: groupMessagesKey(payload.groupId) });
          onGroupGoneRef.current?.(payload.groupId);
          invalidateGroups();
          return;
        }
        invalidateGroupDetail(payload.groupId);
        invalidateGroups();
        return;
      }
      const message = payload;
      appendGroupMessage(message.groupId, message);
      if (!bumpGroupList(message, message.senderUserId === myUserId, Date.now())) invalidateGroups();
      if (message.senderUserId !== myUserId) onIncomingRef.current?.(message);
    });
    return () => {
      offConnect();
      offMessage();
    };
  }, [enabled, myUserId]);
};
